import { Team } from '../types';

import { createYoutubeSearchUrl } from '../utils/videoUtils';
import {
  AiServiceError,
  AiServiceErrorType,
  DetailedDrill,
  GeneratedPracticePlan,
  PracticePlanRequest
} from './aiService.types';
import { BASE_PRACTICE_OUTLINE_PROMPT, DRILL_DETAILS_PROMPT, getSportSpecificPrompt } from './prompts';
import { getModelConfig } from './aiModels';
import { callOpenAI, OpenAIMessage } from '../api/ai';
import { isCooldownSegment, isWarmupSegment, isWaterBreakSegment } from '../../shared/utils/practiceSegments';
import {
  injectMandatoryDrillStubsIntoOutline,
  normalizeDrillDurations,
  normalizePositionGroupArray,
  normalizeStepOptionsForDrill,
  sanitizeAllStepNames,
  validateAndFixPlan,
} from '../../shared/utils/practicePlanPostProcessing';

// Get GPT model from environment variable with fallback
const GPT_MODEL = import.meta.env.VITE_GPT_MODEL || 'gpt-4-turbo-preview';

// Get configuration for current model
const currentModelConfig = getModelConfig(GPT_MODEL);


type PracticeOutlineExtras = {
  date?: string;
  startTime?: string;
  location?: string;
  coachMandatoryDrills?: string[];
};

// Step 1 & 2: Generate practice plan outline
const generatePracticePlanOutline = async (
  team: Team,
  duration: number,
  focusAreas: string[],
  theme: string,
  usePositionGroups: boolean,
  extras?: PracticeOutlineExtras
): Promise<GeneratedPracticePlan> => {
  try {
    // Combine base prompt and sport-specific prompt
    const basePrompt = BASE_PRACTICE_OUTLINE_PROMPT(
      team,
      duration,
      focusAreas,
      theme as any,
      extras?.date,
      extras?.startTime,
      extras?.location,
      {
        usePositionGroups,
        coachMandatoryDrillNames: extras?.coachMandatoryDrills,
      }
    );
    const sportSpecificPrompt = getSportSpecificPrompt(team, duration, focusAreas, theme as any, { usePositionGroups });
    const combinedPrompt = `${basePrompt}\n\n${sportSpecificPrompt}`;

    console.log('[AI Service] Generating practice plan with model:', GPT_MODEL);

    console.log('Outline prompt:', combinedPrompt);
    
    const messages: OpenAIMessage[] = [
      {
        role: 'system',
        content: 'You are an experienced youth sports coach creating detailed practice plans. Follow the exact structure provided.'
      },
      {
        role: 'user',
        content: combinedPrompt
      }
    ];

    const response = await callOpenAI({
      messages,
      model: GPT_MODEL,
      response_format: { type: "json_object" },
      ...(currentModelConfig.useMaxTokens && { max_tokens: currentModelConfig.maxTokens }),
      ...(currentModelConfig.useMaxCompletionTokens && { max_completion_tokens: currentModelConfig.maxTokens }),
      ...(currentModelConfig.supportsTemperature && { temperature: currentModelConfig.temperature })
    }, currentModelConfig.timeout);

    // The response is now the parsed data directly from callOpenAI
    if (!response.choices || !response.choices[0] || !response.choices[0].message) {
      console.error('Unexpected API response structure:', response);
      throw new AiServiceError('Unexpected API response structure', AiServiceErrorType.PARSING_ERROR);
    }
    
    const responseContent = response.choices[0].message.content;
    
    if (!responseContent || responseContent.length === 0) {
      console.error('[AI Service] Empty response content from API');
      throw new AiServiceError('API returned empty response content', AiServiceErrorType.PARSING_ERROR);
    }

    // Validate the response structure
    let parsedResponse;
    try {
      console.log('Raw AI response length:', responseContent.length);
      console.log('Raw AI response (first 500 chars):', responseContent.substring(0, 500));
      console.log('Raw AI response (last 500 chars):', responseContent.substring(Math.max(0, responseContent.length - 500)));
      
      parsedResponse = JSON.parse(responseContent);
    } catch (parseError) {
      console.error('[AI Service] JSON Parse Error:', parseError);
      
      // Try to find and fix common JSON issues
      let fixedContent = responseContent;
      
      // Remove any trailing commas before closing braces/brackets
      fixedContent = fixedContent.replace(/,(\s*[}\]])/g, '$1');
      
      // Ensure the JSON ends properly
      if (!fixedContent.trim().endsWith('}')) {
        fixedContent = fixedContent.trim() + '}';
      }
      
      try {
        parsedResponse = JSON.parse(fixedContent);
      } catch (fixError) {
      throw new AiServiceError(
        `JSON parsing failed: ${parseError instanceof Error ? parseError.message : 'Unknown parse error'}`,
        AiServiceErrorType.PARSING_ERROR
      );
      }
    }
    
    if (!parsedResponse.drills || !Array.isArray(parsedResponse.drills)) {
      throw new AiServiceError('Invalid response structure: missing or invalid drills array', AiServiceErrorType.PARSING_ERROR);
    }

    if (extras?.coachMandatoryDrills?.length) {
      injectMandatoryDrillStubsIntoOutline(parsedResponse, extras.coachMandatoryDrills);
    }

    return parsedResponse;
  } catch (error) {
    console.error('Error in generatePracticePlanOutline:', error);
    
    // Handle timeout errors specifically
    if (error instanceof Error) {
      if (error.name === 'TimeoutError') {
        throw new AiServiceError(
          `API request timed out after ${currentModelConfig.timeout / 1000} seconds. The response may be incomplete. Consider reducing the prompt length or using a faster model.`,
          AiServiceErrorType.API_ERROR
        );
      }
      if (error.message.includes('aborted')) {
        throw new AiServiceError(
          'API request was aborted due to timeout. The response may be incomplete.',
          AiServiceErrorType.API_ERROR
        );
      }
    }
    
    throw new AiServiceError(
      error instanceof Error ? error.message : 'Failed to generate practice plan outline',
      AiServiceErrorType.PARSING_ERROR
    );
  }
};

function inferCoachDrillPositionGroups(team: Team, drillName: string): string[] {
  const usePg = team.aiUsePositionGroups ?? true;
  if (!usePg) return ['ALL'];
  const sport = team.sport;
  if (sport === 'Baseball' || sport === 'Softball') {
    if (/\bpickoff|balk|1st\b|first\s*bas|baseman|pitcher.*hold|hold.*runner/i.test(drillName)) {
      return ['Pitchers', 'Infield'];
    }
    if (/\bbunt|hitting|tee|swing/i.test(drillName)) return ['Hitters', 'Infield'];
  }
  return ['ALL'];
}

/**
 * Expand a single coach-named drill into full step detail (same pipeline as practice plan drill generation).
 */
export async function generateExpandedDrillForPractice(
  team: Team,
  drillName: string,
  durationMinutes: number,
  options?: { desiredDrillStepsPerDrill?: number }
): Promise<Record<string, unknown>> {
  const positionGroupNames = inferCoachDrillPositionGroups(team, drillName);
  const forceAll = !(team.aiUsePositionGroups ?? true);
  const requestedStepCount = Math.min(5, Math.max(2, options?.desiredDrillStepsPerDrill ?? 3));
  const reasonableUpperBound = Math.max(2, Math.floor(durationMinutes / 3));
  const detailStepMinimum = Math.min(requestedStepCount, reasonableUpperBound);

  let detailedDrill: any = await generateDrillDetails(
    drillName,
    positionGroupNames,
    durationMinutes,
    detailStepMinimum,
    team
  );
  detailedDrill.position_groups = normalizePositionGroupArray(detailedDrill.position_groups, forceAll);
  detailedDrill = normalizeStepOptionsForDrill(detailedDrill, detailStepMinimum);
  detailedDrill = sanitizeAllStepNames(detailedDrill);

  const nameLower = (detailedDrill.name || '').toLowerCase();
  const descLower = (detailedDrill.description || '').toLowerCase();
  if (nameLower.includes('competitive') || descLower.includes('competitive')) {
    detailedDrill.intensity_level = 'Game speed';
  }
  if (detailedDrill.intensity_level === undefined || detailedDrill.intensity_level === null) {
    detailedDrill.intensity_level = 'Technique focused';
  }

  if (detailedDrill.position_groups) {
    for (const posGroup of detailedDrill.position_groups) {
      if (typeof posGroup !== 'string' && posGroup.steps && Array.isArray(posGroup.steps)) {
        for (const step of posGroup.steps) {
          step.video_url = createYoutubeSearchUrl(
            step.name,
            team.sport,
            typeof posGroup === 'string' ? posGroup : posGroup.group_name
          );
        }
      }
    }
  }

  return detailedDrill;
}

// Step 3: Generate drill details
const generateDrillDetails = async (
  drillName: string,
  positionGroups: string[],
  drillDuration: number,
  drillStepsMinimum: number,
  team: Team
): Promise<DetailedDrill> => {
  try {
    const prompt = DRILL_DETAILS_PROMPT(drillName, positionGroups, drillDuration, drillStepsMinimum, team);
    
    const messages: OpenAIMessage[] = [
      {
        role: 'system',
        content: 'You are a sports coach providing detailed drill instructions.'
      },
      {
        role: 'user',
        content: prompt
      }
    ];

    const response = await callOpenAI({
      messages,
      model: GPT_MODEL,
      response_format: { type: "json_object" }
    }, currentModelConfig.timeout);

    // Add JSON parsing error handling
    try {
      const responseContent = response.choices[0].message.content;
      const parsedResponse = JSON.parse(responseContent);
      return parsedResponse.drills[0];
    } catch (parseError) {
      console.error('[AI Service] Drill details JSON parsing failed:', parseError);
      throw new AiServiceError(
        `Drill details JSON parsing failed: ${parseError instanceof Error ? parseError.message : 'Unknown parse error'}`,
        AiServiceErrorType.PARSING_ERROR
      );
    }
  } catch (error) {
    throw new AiServiceError(
      error instanceof Error ? error.message : 'Failed to generate drill details',
      AiServiceErrorType.PARSING_ERROR
    );
  }
};

// // Keep the existing findDrillVideo function for backward compatibility, 
// // but make it use the new utility function
// export const findDrillVideo = async (drillName: string, sport: Sport, team: Team, drill?: any): Promise<string> => {
//   try {
//     // Use "tackle football" explicitly if needed
//     const sportTerm = sport === 'Football' ? 'tackle football' : sport;
    
//     // Try to get position group if available from the drill
//     let positionGroupTerm = '';
//     if (drill && drill.position_groups && drill.position_groups.length > 0) {
//       const firstGroup = drill.position_groups[0];
//       positionGroupTerm = typeof firstGroup === 'string' ? firstGroup : 
//                           (firstGroup.group_name ? `${firstGroup.group_name} ` : '');
//     }
    
//     // Create a more structured search query
//     const searchQuery = `${sportTerm} ${positionGroupTerm}coaching drill "${drillName.trim()}"`;
//     console.log(`Finding video for drill with search query: "${searchQuery}"`);
    
//     // Instead of calling OpenAI, directly create a YouTube search URL
//     const youtubeSearchUrl = `https://www.youtube.com/results?search_query=${encodeURIComponent(searchQuery)}`;
//     console.log('Generated YouTube search URL:', youtubeSearchUrl);
    
//     return youtubeSearchUrl;
//   } catch (error) {
//     console.error('Error finding drill video:', error);
//     // Return a search URL as a fallback
//     const sportTerm = sport === 'Football' ? 'tackle football' : sport;
//     return `https://www.youtube.com/results?search_query=${encodeURIComponent(`${sportTerm} drill ${drillName}`)}`;
//   }
// };

// Update the practice plan generator to automatically add video URLs
export const generatePracticePlan = async (
  request: PracticePlanRequest,
  onProgress?: (progress: { 
    status: 'generating' | 'completed' | 'count-update'; 
    drillName?: string; 
    drill?: DetailedDrill;
    totalDrills?: number;
  }) => void
): Promise<GeneratedPracticePlan> => {
  try {
    const detailedDrills: DetailedDrill[] = [];

    // Step 1: Generate practice plan outline
    const outline = await generatePracticePlanOutline(
      request.team,
      request.duration,
      request.focusAreas,
      request.theme,
      request.usePositionGroups ?? request.team.aiUsePositionGroups ?? true,
      {
        date: request.date,
        startTime: request.startTime,
        location: request.location,
        coachMandatoryDrills: request.coachMandatoryDrills,
      }
    );

    // If we have drills, update the progress with the count
    if (outline.drills && outline.drills.length > 0 && onProgress) {
      onProgress({
        status: 'count-update',
        totalDrills: outline.drills.length
      });
    }

    // Step 2: Generate details for each drill
    for (const drill of outline.drills) {
      // Notify UI that we're starting a new drill
      if (onProgress) {
        onProgress({ 
          status: 'generating', 
          drillName: drill.name, 
          drill: undefined 
        });
      }

      let detailedDrill: any;
      
      // Skip generating details for water breaks
      if (isWaterBreakSegment(drill)) {
        detailedDrill = {
          ...drill,
          intensity_level: null, // Water breaks always have null intensity_level
          position_groups: normalizePositionGroupArray(drill.position_groups, true)
        };
        detailedDrill = sanitizeAllStepNames(detailedDrill);
      } else {
        const isWarmup = isWarmupSegment(drill);
        const isCooldown = isCooldownSegment(drill);
        const forceAllGroups = isWarmup || isCooldown || !(request.usePositionGroups ?? request.team.aiUsePositionGroups ?? true);
        const requestedStepCount = Math.min(5, Math.max(1, request.desiredDrillStepsPerDrill ?? 3));
        const reasonableUpperBound = Math.max(2, Math.floor((drill.duration || 10) / 3));
        const detailStepMinimum = isWarmup
          ? Math.max(
              requestedStepCount,
              Math.min(10, Math.max(4, Math.floor((drill.duration || 10) / 2)))
            )
          : Math.min(requestedStepCount, reasonableUpperBound);

        // Generate detailed drill
        detailedDrill = await generateDrillDetails(
          drill.name,
          normalizePositionGroupArray(drill.position_groups, forceAllGroups).map((pg: any) => pg.group_name),
          drill.duration,
          detailStepMinimum,
          request.team
        );
        detailedDrill.position_groups = normalizePositionGroupArray(detailedDrill.position_groups, forceAllGroups);
        detailedDrill = normalizeStepOptionsForDrill(detailedDrill, detailStepMinimum);
        detailedDrill = sanitizeAllStepNames(detailedDrill);
        
        // Preserve intensity_level from outline if detailed drill doesn't have it
        // Or use the detailed drill's intensity_level if it has one
        if (!detailedDrill.intensity_level && drill.intensity_level !== undefined) {
          detailedDrill.intensity_level = drill.intensity_level;
        }
        // Any drill labeled "competitive" gets Game speed
        const nameLower = (detailedDrill.name || '').toLowerCase();
        const descLower = (detailedDrill.description || '').toLowerCase();
        if (nameLower.includes('competitive') || descLower.includes('competitive')) {
          detailedDrill.intensity_level = 'Game speed';
        }
        // Set default if still missing
        if (detailedDrill.intensity_level === undefined || detailedDrill.intensity_level === null) {
          const drillNameLower = (drill.name || '').toLowerCase();
          const isWaterBreak = drillNameLower.includes('water') || 
                              drillNameLower.includes('hydration') || 
                              (drillNameLower.includes('break') && (drillNameLower.includes('rest') || drillNameLower.includes('hydrate')));
          detailedDrill.intensity_level = isWaterBreak ? null : 'Technique focused';
        }
        
        // Generate YouTube search URLs for all steps
        if (detailedDrill.position_groups) {
          for (const posGroup of detailedDrill.position_groups) {
            if (typeof posGroup !== 'string' && posGroup.steps && Array.isArray(posGroup.steps)) {
              for (const step of posGroup.steps) {
                // Generate YouTube search URL for this specific step
                step.video_url = createYoutubeSearchUrl(
                  step.name,
                  request.team.sport,
                  typeof posGroup === 'string' ? posGroup : posGroup.group_name
                );
              }
            }
          }
        }
      }



      // Add the drill to our list
      detailedDrills.push(detailedDrill);

      // Notify UI that this drill is complete
      if (onProgress) {
        onProgress({ status: 'completed', drillName: drill.name, drill: detailedDrill });
      }
    }

    const plan = validateAndFixPlan({
      practice: outline.practice,
      drills: detailedDrills
    }, request.team?.sport);
    normalizeDrillDurations(plan, request.duration);
    return plan;
  } catch (error) {
    throw new AiServiceError(
      error instanceof Error ? error.message : 'Failed to generate practice plan',
      AiServiceErrorType.API_ERROR
    );
  }
};

export const useAIService = () => {
  const generateGamePlan = async (prompt: string): Promise<any> => {
    try {
      const messages: OpenAIMessage[] = [
        {
          role: "system",
          content: "You are a football coach assistant. Generate detailed game plans based on the provided information. Always respond with valid JSON in the exact format requested. Do not wrap the response in markdown code blocks."
        },
        {
          role: "user",
          content: prompt
        }
      ];

      const response = await callOpenAI({
        messages,
        model: GPT_MODEL,
        response_format: { type: "json_object" },
        ...(currentModelConfig.useMaxTokens && { max_tokens: currentModelConfig.maxTokens }),
        ...(currentModelConfig.useMaxCompletionTokens && { max_completion_tokens: currentModelConfig.maxTokens }),
        ...(currentModelConfig.supportsTemperature && { temperature: currentModelConfig.temperature })
      }, currentModelConfig.timeout);

      console.log('[AI Service] Raw OpenAI response:', response);

      const content = response.choices[0]?.message?.content;
      if (!content) {
        console.error('[AI Service] No content in AI response');
        throw new Error('No content in AI response');
      }

      console.log('[AI Service] AI response content:', content);

      try {
        // Strip markdown code blocks if present
        const cleanContent = content.replace(/```json\n?|\n?```/g, '').trim();
        
        // const parsedContent = JSON.parse(cleanContent);
        return cleanContent; // Return the cleaned content for the component to parse
      } catch (e) {
        console.error('[AI Service] Failed to parse AI response as JSON:', e);
        throw new Error('Failed to parse AI response as JSON');
      }
    } catch (error) {
      console.error('[AI Service] Error in generateGamePlan:', error);
      throw error;
    }
  };

  return {
    generateGamePlan
  };
}; 